/**
 * Rules for the application/channel grants edited in the user drawer.
 *
 * A channel belongs to exactly one application, and an application grant is
 * only useful alongside at least one of its channels. Channels are matched by
 * `application_id` only; channel names such as "Admin" repeat across apps.
 */

export interface ChannelRef {
  id: string | number | null | undefined;
  application_id: string | number | null | undefined;
}

const channelsById = (channelList: ChannelRef[]) => {
  const byId = new Map<string, ChannelRef>();
  for (const channel of channelList ?? []) {
    if (channel?.id == null) continue;
    byId.set(String(channel.id), channel);
  }
  return byId;
};

/**
 * Selected application ids that have none of their channels selected.
 * Channel ids that are not in `channelList` (stale or deleted) count for
 * nothing.
 */
export const appsMissingChannels = (
  appIds: Array<string | number>,
  channelIds: Array<string | number>,
  channelList: ChannelRef[],
): string[] => {
  const byId = channelsById(channelList);
  const coveredAppIds = new Set<string>();
  for (const channelId of channelIds ?? []) {
    const channel = byId.get(String(channelId));
    if (channel?.application_id == null) continue;
    coveredAppIds.add(String(channel.application_id));
  }
  const missing = (appIds ?? [])
    .map(String)
    .filter((appId) => !coveredAppIds.has(appId));
  return Array.from(new Set(missing));
};

/** Selected application ids that have no channels at all in `channelList`. */
export const appsWithNoChannelsConfigured = (
  appIds: Array<string | number>,
  channelList: ChannelRef[],
): string[] => {
  const appsWithChannels = new Set<string>();
  for (const channel of channelList ?? []) {
    if (channel?.id == null || channel.application_id == null) continue;
    appsWithChannels.add(String(channel.application_id));
  }
  const missing = (appIds ?? [])
    .map(String)
    .filter((appId) => !appsWithChannels.has(appId));
  return Array.from(new Set(missing));
};

/**
 * Inline error messages for the channel selector, or [] when the selection is
 * valid. Apps with no configured channels get their own message rather than
 * being listed as needing a channel the admin cannot pick.
 */
export const getAppChannelErrors = (
  appIds: Array<string | number>,
  channelIds: Array<string | number>,
  channelList: ChannelRef[],
  appName: (appId: string) => string,
): string[] => {
  const unconfigured = appsWithNoChannelsConfigured(appIds, channelList);
  const unconfiguredSet = new Set(unconfigured);
  const needsChannel = appsMissingChannels(appIds, channelIds, channelList).filter(
    (appId) => !unconfiguredSet.has(appId),
  );
  const messages: string[] = [];
  if (needsChannel.length > 0) {
    messages.push(
      `Select at least one channel for: ${needsChannel.map(appName).join(", ")}.`,
    );
  }
  for (const appId of unconfigured) {
    messages.push(
      `${appName(appId)} has no channels configured, so it can't be granted. Remove it or add a channel to it in Drawer Structure.`,
    );
  }
  return messages;
};

export interface ApplicationRef {
  id: string | number | null | undefined;
}

/**
 * The application and channel ids to send when saving a user's access.
 *
 * Drops ids the drawer does not know about (deleted applications or channels
 * still stored on the user) and channels whose application is not selected
 * (e.g. a channel since moved to another application), so legacy data does
 * not make the server reject the save. If a list failed to load it is not
 * used to filter, rather than dropping everything.
 */
export const sanitizeAccessSelection = (
  appIds: Array<string | number>,
  channelIds: Array<string | number>,
  channelList: ChannelRef[],
  applicationList: ApplicationRef[],
): { applications: string[]; channels: string[] } => {
  const knownAppIds = new Set(
    (applicationList ?? []).filter((a) => a?.id != null).map((a) => String(a.id)),
  );
  const applications = Array.from(
    new Set(
      (appIds ?? [])
        .map(String)
        .filter((id) => knownAppIds.size === 0 || knownAppIds.has(id)),
    ),
  );

  const byId = channelsById(channelList);
  const selectedApps = new Set(applications);
  const channels = Array.from(
    new Set(
      (channelIds ?? []).map(String).filter((id) => {
        if (byId.size === 0) return true;
        const channel = byId.get(id);
        return channel?.application_id != null && selectedApps.has(String(channel.application_id));
      }),
    ),
  );

  return { applications, channels };
};
